import { useReducedMotion } from '@mantine/hooks'
import { useAtomValue } from 'jotai'
import { Box } from '@mantine/core'
import { GlowAvatar } from './GlowAvatar'
import { activeSpeakerIdAtom, currentVisemeAtom } from '../../atoms/speechAtoms'
import { VISEME_SHAPE } from './visemeShapes'

// Shared mouth-overlay element. `base` is the reference dimension the mouth is
// sized against (avatar diameter, or the smaller side of a fill portrait).
function Mouth({ shape, base }) {
  const w = base * 0.34 * (0.7 + shape.wide * 0.6)
  const h = Math.max(2, base * 0.18 * shape.openY)
  return (
    <Box
      aria-hidden
      style={{
        position: 'absolute',
        left: '50%',
        top: '68%',
        width: w,
        height: h,
        transform: 'translate(-50%, -50%)',
        borderRadius: '50%',
        background: 'rgba(20, 6, 10, 0.82)',
        boxShadow: 'inset 0 -1px 2px rgba(0,0,0,0.6)',
        transition: 'width 70ms linear, height 70ms linear',
        pointerEvents: 'none',
      }}
    />
  )
}

/**
 * A character portrait that animates its mouth in time with speech. When this
 * character is the active speaker (activeSpeakerIdAtom), an overlaid mouth opens
 * and closes following currentVisemeAtom (driven by useSpeech from the Polly
 * viseme stream).
 *
 * Two render modes:
 *  - default: a round/square GlowAvatar at `size` px (cast dock, small headers).
 *  - fill: a large rectangular portrait that fills its parent (hero / scene
 *    takeover, chat header banner). Parent controls the box; this fills it.
 *
 * Respects prefers-reduced-motion (no mouth) and a per-character
 * `lipsync: false` opt-out (character.options.lipsync === false).
 */
export function LipSyncPortrait({
  character,
  size = 50,
  active = false,
  square = false,
  fill = false,
}) {
  const activeSpeakerId = useAtomValue(activeSpeakerIdAtom)
  const viseme = useAtomValue(currentVisemeAtom)
  const reduceMotion = useReducedMotion()

  const isSpeaking = !!character && activeSpeakerId === character.id
  const lipsyncOptOut = character?.options?.lipsync === false
  const showMouth = isSpeaking && !reduceMotion && !lipsyncOptOut
  const shape = VISEME_SHAPE[showMouth ? viseme : 'sil'] || VISEME_SHAPE.sil

  if (fill) {
    // Large portrait filling the parent. At hero size the speech indicator is
    // the SpeechVisualizer rendered BELOW the portrait (see SceneWindow /
    // ChatPanel), not an overlaid mouth — landing a generic ellipse precisely on
    // a real character's mouth doesn't read well, whereas a visualizer sells
    // "this character is speaking" without needing to match the art.
    return (
      <Box style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
        {character?.avatar_url ? (
          <Box
            style={{
              position: 'absolute',
              inset: 0,
              background: `center top / cover no-repeat url(${character.avatar_url})`,
            }}
          />
        ) : (
          <Box
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 72,
              fontWeight: 700,
              color: 'var(--bw-accent, #00e5ff)',
              background: 'rgba(0,0,0,0.4)',
            }}
          >
            {character?.name?.[0]?.toUpperCase()}
          </Box>
        )}
      </Box>
    )
  }

  return (
    <Box style={{ position: 'relative', display: 'inline-flex', lineHeight: 0 }}>
      <GlowAvatar
        src={character?.avatar_url}
        name={character?.name}
        size={size}
        active={active || isSpeaking}
        square={square}
      />
      {showMouth && <Mouth shape={shape} base={size} />}
    </Box>
  )
}
